/* eslint-disable react/jsx-key */
import React, { useEffect, useMemo, useState } from "react";
import { TaskType, RadioButtonMultipleStepAnswer, RadioButtonMultipleStepTask } from "data-models/task-models";
import { QUESTION_ACTIONS, useFollowUp } from "providers/FollowUpProvider";
import {
  Tab,
  Tabs,
  Flex,
  Image,
  VStack,
  HStack,
  Center,
  TabList,
  TabPanel,
  TabPanels,
  Box,
  Text,
  RadioGroup,
  Radio,
  Grid,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { apiBase } from "services/config";
import { MultiTaskHeading } from "./Common/TaskHeading";

export default function RadioButtonMultipleSteps() {
  const { t } = useTranslation();
  const { currentTask, dispatch } = useFollowUp();
  const task = currentTask as RadioButtonMultipleStepTask;

  const [animatingId, setAnimatingId] = useState<RadioButtonMultipleStepAnswer["id"] | null>(null);

  const imagesPerStep = Math.max(1, Number(task.imagesPerStep ?? 1));

  const imageOptions = useMemo(
    () =>
      (task.answerOptions ?? []).filter(
        (ao: any) =>
          ao.type === TaskType.RadioButtonMultipleSteps &&
          typeof ao.imageUrl === "string" &&
          ao.imageUrl.length > 0 &&
          ao.displayImage,
      ),
    [task.answerOptions],
  );

  const totalSteps = Math.max(1, Math.ceil(imageOptions.length / imagesPerStep));

  const currentStepIndex = Math.min(Math.max(0, Number(currentTask.step ?? 0)), totalSteps - 1);

  const start = currentStepIndex * imagesPerStep;
  const end = start + imagesPerStep;

  const visibleOptions = useMemo(() => imageOptions.slice(start, end), [imageOptions, start, end]);

  useEffect(() => {
    if (Number(currentTask.step ?? 0) !== currentStepIndex) {
      dispatch({ type: QUESTION_ACTIONS.SET_CURRENT_STEP, payload: currentStepIndex });
    }
  }, [totalSteps]);

  const handleRadioClick = (id: string, img: string, value: string) => {
    //storing the correct answer option id
    const correctAnswerOptionId = task.answerOptions
      .filter(ao => ao.imageUrl === img && ao.isCorrect === true)
      .map(obj => obj.id);

    const visibleIds = task.answerOptions.filter((v: any) => v.imageUrl === img).map(vi => vi.id);

    if (value === "answered") {
      onSelectedChange(correctAnswerOptionId[0], img, true);
    } else {
      //getting the incorrect option id from the visibleIds
      let incorrectAnswerOptionId = visibleIds.filter(vi => vi !== correctAnswerOptionId[0]);
      onSelectedChange(incorrectAnswerOptionId[0], img, true);
    }
  };

  function onSelectedChange(changeId: string, img: string, answerValue?: boolean) {
    const wasChecked = task.answerOptions.find((ao: any) => ao.id === changeId)?.checked;

    // ids that belong to the current page
    const visibleIds = new Set(task.answerOptions.filter((v: any) => v.imageUrl === img).map(vi => vi.id));

    const newOptionsState = task.answerOptions.map((ao: any) => {
      // only enforce single-select inside the current page
      if (!visibleIds.has(ao.id)) return ao;

      // clicked item: toggle
      if (ao.id === changeId) {
        return { ...ao, checked: answerValue, isAnswered: true };
      }

      // other items on the same page: always uncheck
      return { ...ao, checked: false, isAnswered: true };
    });

    // animation logic (only if we turned it ON)
    if (!wasChecked) {
      setAnimatingId(changeId);
      window.setTimeout(() => setAnimatingId(null), 300);
    } else {
      setAnimatingId(null);
    }

    dispatch({
      type: QUESTION_ACTIONS.UPDATE_ANSWERS,
      payload: newOptionsState,
    });
    dispatch({ type: QUESTION_ACTIONS.UNSKIP_CURRENT_TASK });
  }

  const onChangeTab = (index: number) => {
    dispatch({ type: QUESTION_ACTIONS.SET_CURRENT_STEP, payload: index });
  };

  const { i18n } = useTranslation();

  const instructionKey = (idx: number) => (["a", "b", "c", "d"][idx] ?? "a") as "a" | "b" | "c" | "d";

  const localizedStepInstruction = (() => {
    const key = instructionKey(currentStepIndex);
    const arr = (task.taskInstructions as any)?.[key] as [string, string, string] | undefined;

    if (!arr) return "";
    if (i18n.language === "en") return arr[1] ?? arr[0];
    if (i18n.language === "de") return arr[2] ?? arr[0];
    return arr[0];
  })();

  return (
    <VStack pos="relative" h="full" w="full" p="5">
      <MultiTaskHeading description={task.description} headingTitle={task.title} />

      <Tabs h="full" w="full" pos="relative" variant="unstyled" onChange={onChangeTab} index={currentStepIndex}>
        <TabList pb="4" pt="3">
          <Flex
            h="0"
            w="full"
            pos="absolute"
            alignSelf="center"
            justifySelf="center"
            borderTop="1px solid #2A0F74"
            zIndex={0}
          />

          {Array.from({ length: totalSteps }, (_, i) => (
            <Tab
              key={i}
              px={4}
              py={2}
              mr={6}
              zIndex={1}
              w="180px"
              border="1px solid #2A0F74"
              fontSize="xs"
              borderRadius="8px"
              bgColor="#6c40f6"
              _selected={{ bgColor: "#2A0F74", fontWeight: "700" }}
              _focus={{ boxShadow: "none" }}
            >
              <span style={{ opacity: 0.5, paddingRight: "10px" }}>
                {t("Step")} {i + 1}
              </span>
            </Tab>
          ))}
        </TabList>

        <TabPanels h="full" w="full" py={0}>
          {Array.from({ length: totalSteps }, (_, stepIdx) => (
            <TabPanel key={stepIdx} w="full" h="full" maxH="full">
              <HStack w="full" h="100%" justify="center" align="center" mt="-20px">
                {(stepIdx === currentStepIndex ? visibleOptions : []).map((ao: any) => (
                  <VStack align="center" justify="center" h={{ base: "auto", lg: "50%" }} w="full">
                    <>
                      <Center
                        w={task.imagesPerStep === 1 ? "450px" : "260px"}
                        h={task.imagesPerStep === 1 ? "300px" : "200px"}
                        pos="relative"
                        bgColor="white"
                        borderRadius="xl"
                        borderWidth="4px"
                        tabIndex={0}
                      >
                        <Image src={apiBase + ao.imageUrl} maxH="100%" maxW="100%" />
                      </Center>
                      <Grid w="full" templateColumns="1fr auto 1fr" pt="20px" alignItems="start">
                        <HStack align="flex-start" mt="18px">
                          <Text lineHeight="18px" fontSize="sm" fontWeight="bold" whiteSpace="nowrap">
                            {t("Note_Text")}:
                          </Text>
                          <Box
                            lineHeight="18px"
                            fontSize="sm"
                            dangerouslySetInnerHTML={{ __html: localizedStepInstruction }}
                          />
                        </HStack>
                        <Center>
                          <RadioGroup
                            value={ao.isAnswered ? (ao.checked === true ? "answered" : "didnotanswer") : undefined}
                            onChange={value => handleRadioClick(ao.id, ao.imageUrl, value)}
                            color="black"
                            outline="none"
                            box-shadow="none !important"
                          >
                            <HStack mt="20px">
                              <Radio value="answered" colorScheme="pink" pb="3">
                                {t("Answered_Correctly")}
                              </Radio>
                              <Radio value="didnotanswer" colorScheme="pink" pb="3">
                                {t("Answered_Incorrectly")}
                              </Radio>
                            </HStack>
                          </RadioGroup>
                        </Center>
                        <Box />
                      </Grid>
                    </>
                  </VStack>
                ))}
              </HStack>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
